import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Col, Modal, Popover, Row, Space, Spin, Steps, Table, Tag, Typography, message } from 'antd'
import adminService, { NewsletterPreviewItem, NewsletterPreviewResponse } from '../../../services/adminService'
import { Research, ResearchSend } from '../../../services/models'
import ResearchComponent from '../../../components/ResearchComponent/ResearchComponent'
import { ResearchForm, ResearchFormHandle, ResearchCreateFields } from '../../../components/ResearchForm/ResearchForm'
import AdminPageTitle from '../../../components/AdminPageTitle/AdminPageTitle'
import { Helmet } from 'react-helmet-async'
import dayjs from 'dayjs'
import { BackwardOutlined, CheckOutlined, EditOutlined } from '@ant-design/icons'

// Причину исключения бэк отдаёт кодом — подписи держим здесь, чтобы их не
// продублировать в таблице и чтобы новый код не ломал рендер.
const REASON_LABELS: Record<string, string> = {
  no_consent: 'нет согласия на рассылку',
  deleted: 'аккаунт удалён'
}

const reasonLabel = (reason: NewsletterPreviewItem['reason']) =>
  reason ? (REASON_LABELS[reason] || reason) : ''

export const AdminResearchPage: React.FC = () => {
  const { researchId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [research, setResearch] = useState<Research>()
  const [sends, setSends] = useState<ResearchSend[]>([])
  const [sendModalOpen, setSendModalOpen] = useState<boolean>(false)
  const [preview, setPreview] = useState<NewsletterPreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(searchParams.get('mode') === 'edit')
  const formRef = useRef<ResearchFormHandle>(null)

  const fetchResearch = () => {
    if (!researchId) {
      return null
    }
    adminService.getResearchById(+researchId).then(data => {
      setResearch(data.data)
    })
  }

  const fetchSends = (id: number) => {
    adminService.getResearchSends(id).then(data => {
      setSends(data.items)
    })
  }

  useEffect(() => {
    fetchResearch()
  }, [])

  useEffect(() => {
    if (research?.sentAt) {
      fetchSends(research.id)
    }
  }, [research?.sentAt])

  const onPublish = () => {
    if (!research) {
      return
    }
    adminService.publishResearch(research.id).then(data => {
      setResearch(data.data)
      message.success('Исследование опубликовано')
    }).catch(() => {
      message.error('Ошибка публикации')
    })
  }

  // Аудиторию грузим ДО показа модалки: аналитик должен видеть, кому уйдёт
  // письмо, а не узнавать об этом постфактум. Текст ошибки берём с бэка — в
  // частности это «нет получателей», который иначе выглядит как общий сбой.
  // Optional chaining обязателен: при сетевой ошибке error.response не существует.
  const onOpenSendModal = () => {
    if (!research) {
      return
    }
    setSendModalOpen(true)
    setPreview(null)
    setPreviewLoading(true)
    adminService.previewResearchNewsletter(research.id)
      .then((data) => setPreview(data))
      .catch((error) => {
        setSendModalOpen(false)
        message.error(error?.response?.data?.message || 'Не удалось загрузить список получателей')
      })
      .finally(() => setPreviewLoading(false))
  }

  const onConfirmSend = () => {
    if (!research || !preview) {
      return
    }
    // Отправка и повторная отправка — это два разных эндпоинта. Первая доступна,
    // пока рассылка ни разу не уходила, вторая — только после успешной первой.
    // Режим берём из того же превью, по которому считалась кнопка, чтобы
    // запрос и подпись кнопки всегда смотрели на одно состояние.
    const isResend = !!preview.sentAt
    setSending(true)
    const request = isResend
      ? adminService.resendResearchNewsletter(research.id)
      : adminService.sendResearchNewsletter(research.id)
    request
      .then((data) => {
        const prefix = isResend ? 'Рассылка отправлена повторно' : 'Рассылка отправлена'
        message.success(`${prefix}: успешно ${data.sent}, ошибок ${data.failed}`)
        setSendModalOpen(false)
        fetchResearch()
        fetchSends(research.id)
      })
      .catch((error) => {
        message.error(error?.response?.data?.message || 'Ошибка отправки рассылки')
      })
      .finally(() => setSending(false))
  }

  const onUpdateResearch = (fields: ResearchCreateFields, imageDescription: { [k: number]: string }) => {
    if (!research || !fields.serviceId || !fields.userId) {
      return
    }
    adminService.updateResearch(
      research.id,
      fields.title,
      fields.body,
      +fields.serviceId,
      +fields.userId,
      imageDescription
    ).then(() => {
      message.success('Исследование успешно обновлено')
      fetchResearch()
      setEditMode(false)
    }).catch(() => {
      message.error('Ошибка обновления исследования')
    })
  }

  const onDelete = () => {
    if (!research) {
      return
    }
    // Удаление необратимо, поэтому спрашиваем подтверждение — по образцу
    // onSend. Раньше кнопка удаляла документ в один клик.
    Modal.confirm({
      title: 'Удалить исследование?',
      content: `«${research.title}» будет удалено без возможности восстановления.`,
      okText: 'Удалить',
      okButtonProps: { danger: true },
      onOk: () => adminService.removeResearch(research.id).then((data) => {
        if (data) {
          message.success('Исследование успешно удалено')
        } else {
          message.error('Ошибка удаления исследования')
        }
        navigate('/researches')
      }).catch(() => {
        message.error('Ошибка удаления исследования')
      })
    })
  }

  const isPublished = !!research?.isPublished
  const isSent = !!research?.sentAt
  // Журнал хранит каждую попытку отдельной строкой, поэтому «из N» больше не
  // работает: после повторной отправки строк вдвое больше, чем получателей.
  // Показываем число попыток, а не число записей.
  const attemptCount = sends.length ? Math.max(...sends.map((s) => s.attempt)) : 0
  // Воронка документа считается из уже загруженных полей — лишних запросов
  // не делаем. Черновик = 0, опубликовано = 1, разослано = 2.
  const currentStep = isSent ? 2 : isPublished ? 1 : 0
  const formatDate = (date?: string | null) => date
    ? dayjs(date).format('DD/MM/YYYY HH:mm')
    : '—'
  // В модалке одна кнопка на два разных действия: пока рассылка не уходила она
  // отправляет, после — повторяет. Отсюда и подпись, и предупреждение о том,
  // что письмо придёт в том числе тем, кому оно уже доходило.
  const isResendMode = !!preview?.sentAt

  if (!research) {
    return <div>Loading...</div>
  }

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Имя',
      key: 'name',
      render: (_: any, record: ResearchSend) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => status === 'sent'
        ? <Tag color="green">доставлено</Tag>
        : <Tag color="red">ошибка</Tag>
    },
    {
      title: 'Попыток',
      dataIndex: 'attempt',
      key: 'attempt',
      width: 90
    },
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Ошибка',
      dataIndex: 'error',
      key: 'error',
      render: (error: string | null) => error
        ? <Popover content={error}><Typography.Link>подробнее</Typography.Link></Popover>
        : '—'
    }
  ]

  // Превью и журнал отправок говорят об одном и том же разными словами: письмо
  // уходит по доступу, лента — по подписке. Поэтому «Письмо» и «Лента ЛК» это
  // две отдельные колонки, иначе аналитик снова решит, что подписка решает всё.
  const previewColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Имя',
      key: 'name',
      render: (_: any, record: NewsletterPreviewItem) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Письмо',
      key: 'willSend',
      render: (_: any, record: NewsletterPreviewItem) => record.willSend
        ? <Tag color="green">получит</Tag>
        : <Tag color="red">{reasonLabel(record.reason)}</Tag>
    },
    {
      title: 'Лента ЛК',
      key: 'feed',
      render: (_: any, record: NewsletterPreviewItem) => record.isSubscribed
        ? <Tag color="blue">увидит</Tag>
        : <Tag>не увидит</Tag>
    },
    // Отвечает на вопрос «кому уже уходило и когда» — иначе повторная отправка
    // выглядит вслепую. «Получит» выше отвечает на будущее, эта колонка — на
    // прошлое, поэтому держать их рядом и не сливать.
    {
      title: 'Доставлено',
      key: 'delivery',
      render: (_: any, record: NewsletterPreviewItem) => {
        if (record.deliveredAt) {
          return (
            <Space size={4}>
              <Tag color="green">{formatDate(record.deliveredAt)}</Tag>
              {record.attempts > 1 && (
                <Typography.Text type="secondary">попыток: {record.attempts}</Typography.Text>
              )}
            </Space>
          )
        }
        if (record.attempts > 0) {
          return <Tag color="red">не дошло</Tag>
        }
        return <Typography.Text type="secondary">не отправлялось</Typography.Text>
      }
    }
  ]

  return (
      <div>
        <Helmet><title>{`Prisma — ${research.title}`}</title></Helmet>
        <AdminPageTitle
            title={research.title}
            buttonText={'Назад'}
            icon={<BackwardOutlined/>}
            onClickButton={() => navigate('/researches')}
            extra={isPublished
              ? <Tag color={isSent ? 'default' : 'cyan'}>{isSent ? 'Рассылка отправлена' : 'Опубликовано'}</Tag>
              : <Tag>Черновик</Tag>}
        />
        <Steps
            size="small"
            style={{ marginBottom: 20 }}
            current={currentStep}
            items={[
              { title: 'Черновик' },
              { title: 'Опубликовано' },
              { title: 'Рассылка отправлена' }
            ]}
        />
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[16, 8]}>
          <Col>
            <Typography.Text type="secondary">
              Создано: {formatDate(research.createdAt)} · Обновлено: {formatDate(research.updatedAt)}
            </Typography.Text>
          </Col>
          <Col>
            <Space>
              {editMode
                ? (
                <>
                  <Button
                    type="primary"
                    icon={<CheckOutlined/>}
                    onClick={() => formRef.current?.submit()}
                  >
                    Сохранить
                  </Button>
                  <Button onClick={() => setEditMode(false)}>Отмена</Button>
                </>
                  )
                : (
                <>
                  {!isPublished && (
                    <Button type="primary" onClick={onPublish}>Опубликовать</Button>
                  )}
                  {isPublished && (
                    <Button type="primary" onClick={onOpenSendModal}>
                      {isSent ? 'Отправить повторно' : 'Отправить рассылку'}
                    </Button>
                  )}
                  <Button
                    icon={<EditOutlined/>}
                    onClick={() => setEditMode(true)}
                  >
                    Редактировать
                  </Button>
                  <Button danger onClick={onDelete}>Удалить</Button>
                </>
                  )}
            </Space>
          </Col>
        </Row>
        <Space style={{ width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
<div style={{ flexGrow: 1 }}>
          {editMode
            ? <ResearchForm ref={formRef} data={research} onSubmit={onUpdateResearch} onDelete={onDelete} showActions={false}/>
            : <ResearchComponent research={research} showTitle={false}/>}
        </div>
        </Space>
        {isSent && (
            <Card title={`История отправок — попыток: ${attemptCount}`} style={{ marginTop: 16 }}>
              <Table
                  rowKey={(record) => record.email}
                  dataSource={sends}
                  columns={columns}
                  pagination={false}
              />
            </Card>
        )}
        <Modal
          open={sendModalOpen}
          title={isResendMode ? 'Отправить рассылку повторно' : 'Отправить рассылку'}
          onCancel={() => setSendModalOpen(false)}
          okText={isResendMode ? 'Отправить повторно' : 'Отправить'}
          onOk={onConfirmSend}
          confirmLoading={sending}
          // Блокируем, когда заведомо ничего не выйдет: отправлять некому либо
          // исследование ещё не опубликовано — в обоих случаях бэк вернёт 400.
          // Состояние «уже отправлено» больше не блокирует, оно переключает
          // кнопку в режим повторной отправки через `isResendMode`.
          okButtonProps={{
            disabled: previewLoading || !preview || preview.willSend === 0 || !preview.isPublished
          }}
          width={760}
        >
          <Spin spinning={previewLoading}>
            {!preview
              ? <Typography.Text type="secondary">Загрузка аудитории…</Typography.Text>
              : (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {!preview.isPublished && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Исследование не опубликовано"
                      description="Отправка вернёт ошибку — сначала опубликуйте исследование."
                    />
                  )}
                  {isResendMode && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Это повторная отправка"
                      description={`Письмо уйдёт всей аудитории заново, включая тех, кому оно уже доходило: таких ${preview.delivered} из ${preview.willSend}. Предыдущая отправка — ${formatDate(preview.sentAt)}.`}
                    />
                  )}
                  {preview.willSend === 0 && (
                    <Alert
                      type="error"
                      showIcon
                      message="Нет получателей"
                      description="Ни у кого с доступом к этому сервису нет согласия на рассылку. Письма уходят всем, у кого есть доступ, — подписка на письма не влияет."
                    />
                  )}
                  <Typography.Text>
                    {preview.serviceName ? `Услуга: ${preview.serviceName}. ` : ''}
                    Письмо получат <b>{preview.willSend}</b> из <b>{preview.total}</b> клиентов с доступом,
                    не получат <b>{preview.excluded}</b>.{' '}
                    {isResendMode
                      ? `Из них раньше получали ${preview.delivered}, ни разу — ${preview.pending}.`
                      : `Из них ни разу не получали: ${preview.pending}.`}
                  </Typography.Text>
                  <Table
                    rowKey={(record) => record.id}
                    dataSource={preview.items}
                    columns={previewColumns}
                    pagination={false}
                    size="small"
                  />
                </Space>
                )}
          </Spin>
        </Modal>
      </div>

  )
}
